import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { formatDate, formatToZMW } from "../../../helpers";
import { FaSpinner } from "react-icons/fa";
import { calculateIncomeStatement } from "../../../utils/reportCalculations";

const DetailedIncomeStatement = ({ rdata, startDate, endDate }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const { allData } = useOutletContext();

  useEffect(() => {
    if (!rdata?.bills) return;

    setResult(calculateIncomeStatement(rdata, allData, { cumulative: false }));
    setLoading(false);
  }, [rdata, allData]);

  const formatAmount = (amount) => formatToZMW((amount || 0).toFixed(2));

  return (
    <div className="w-full">
      {loading || !result ? (
        <div className="flex justify-center items-center h-full w-full text-blue-950 text-5xl p-5 m-5">
          <FaSpinner size={50} className="spinner" />
        </div>
      ) : (
        <div className="container mx-auto p-4">
          <div className="text-center">
            <h2 className="font-bold text-2xl">{allData.council[0]?.name.toUpperCase()}</h2>
            <h2 className="font-bold text-2xl my-3">INCOME STATEMENT</h2>
            <h3 className="text-xl font-bold">
              From {formatDate(startDate)} to {formatDate(endDate)}
            </h3>
          </div>
          <table id="detailed-income-statement" className="w-full g-table">
            <thead className="bg-blue-950 text-white">
              <tr>
                <th className="text-left py-2 px-2">Code</th>
                <th className="text-left py-2 px-2">Item</th>
                <th className="text-right py-2 px-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gray-800 text-white font-bold">
                <td className="py-2 px-2" colSpan={3}>Revenue</td>
              </tr>
              {result.lines
                .filter((item) => item.type === "revenue")
                .map((item) => (
                  <tr key={`revenue-${item.code}`}>
                    <td className="py-2 px-2">{item.code}</td>
                    <td className="py-2 px-2">{item.name}</td>
                    <td className="py-2 px-2 text-right">{formatAmount(item.amount)}</td>
                  </tr>
                ))}
              <tr className="bg-blue-950 text-white font-semibold">
                <td className="py-2 px-2" colSpan={2}>Total Revenue</td>
                <td className="py-2 px-2 text-right">{formatAmount(result.totalRevenue)}</td>
              </tr>

              <tr className="bg-gray-800 text-white font-bold">
                <td className="py-2 px-2" colSpan={3}>Expense</td>
              </tr>
              {result.lines
                .filter((item) => item.type === "expense")
                .map((item) => (
                  <tr key={`expense-${item.code}`}>
                    <td className="py-2 px-2">{item.code}</td>
                    <td className="py-2 px-2">{item.name}</td>
                    <td className="py-2 px-2 text-right">{formatAmount(item.amount)}</td>
                  </tr>
                ))}
              <tr className="bg-blue-950 text-white font-semibold">
                <td className="py-2 px-2" colSpan={2}>Total Expense</td>
                <td className="py-2 px-2 text-right">{formatAmount(result.totalExpenses)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <th className="text-left py-2 px-2" colSpan={2}>Surplus (Deficit) for period</th>
                <th className="text-right py-2 px-2">{formatAmount(result.netIncome)}</th>
              </tr>
              <tr>
                <td className="py-2 px-2" colSpan={2}>Surplus (Deficit) brought forward</td>
                <td className="py-2 px-2 text-right">{formatAmount(result.openingSurplus)}</td>
              </tr>
              <tr>
                <td className="py-2 px-2" colSpan={2}>Opening cash (Accumulated Fund)</td>
                <td className="py-2 px-2 text-right">{formatAmount(result.openingCash)}</td>
              </tr>
              <tr className="bg-gray-200 font-bold">
                <th className="text-left py-2 px-2" colSpan={2}>Total Surplus (Deficit) carried forward</th>
                <th className="text-right py-2 px-2">{formatAmount(result.totalSurplus)}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default DetailedIncomeStatement;
